/**
 * Year / Date-Range toggle group with the accompanying date selector.
 * Used by CompanyGHGPage, GHGOverviewPage, CompanyStatisticsPage, StatisticsOverviewPage.
 */

import React from 'react';
import { Box, Divider, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { CalendarMonth, DateRange } from '@mui/icons-material';
import YearSelector from '../../components/shared/YearSelector';
import DateRangeSelector from '../../components/shared/DateRangeSelector';

export interface PeriodFilterToggleProps {
  filterMode: 'year' | 'dateRange';
  onFilterModeChange: (mode: 'year' | 'dateRange') => void;
  selectedYear: number;
  onYearChange: (year: number) => void;
  years: number[];
  fromDate: Date | null;
  toDate: Date | null;
  onFromDateChange: (date: Date | null) => void;
  onToDateChange: (date: Date | null) => void;
  /** Extra sx props forwarded to YearSelector. Optional. */
  yearSelectorSx?: object;
}

const PeriodFilterToggle: React.FC<PeriodFilterToggleProps> = ({
  filterMode,
  onFilterModeChange,
  selectedYear,
  onYearChange,
  years,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  yearSelectorSx,
}) => (
  <>
    <ToggleButtonGroup
      value={filterMode}
      exclusive
      onChange={(_, v) => v && onFilterModeChange(v)}
      size="small"
      sx={{ width: 250, height: 54 }}
    >
      <ToggleButton value="year" sx={{ flex: 1 }}>
        <CalendarMonth sx={{ mr: 1, fontSize: 18 }} />
        Year
      </ToggleButton>
      <ToggleButton value="dateRange" sx={{ flex: 1 }}>
        <DateRange sx={{ mr: 1, fontSize: 18 }} />
        Date Range
      </ToggleButton>
    </ToggleButtonGroup>

    <Divider orientation="vertical" flexItem />

    <Box display="flex" alignItems="center" gap={2}>
      {filterMode === 'year' ? (
        <YearSelector
          value={selectedYear}
          onChange={onYearChange}
          years={years}
          sx={yearSelectorSx}
        />
      ) : (
        <DateRangeSelector
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={onFromDateChange}
          onToDateChange={onToDateChange}
          maxDate={new Date()}
          // Match the 54px toggle group beside it rather than the 40px default.
          sx={{ '--cc-dp-control-h': '54px' }}
        />
      )}
    </Box>
  </>
);

export default PeriodFilterToggle;
